import Big from 'big.js';
import {API_URLS} from '@/const';
import {http} from '@/helper/http';
import {numUtil} from '@/helper/num';
import {contractUtil} from '@/helper/contract';

const DEFAULT_USD_PRICE_SCALE = 4;

export const configApi = {
  getProductList() {
    return http.get(API_URLS.CONFIG_PRODUCTS, {
      formatter(data) {
        let {
          currencies,
          products,
          riskLimits,
          leverages,
          leveragesV2 = [],
          perpProductsV2 = [],
          riskLimitsV2 = [],
          collaterals = [],
          marginRiskLevels,
          tradeLevels,
          leverageMargins = [],
          perpProductsPilot = [],
        } = data;
        leverages = leverages || [];
        collaterals = collaterals || [];

        const currenciesData = convertCurrencies(currencies);

        const contracts = [...products.filter((o) => o.type === 'Perpetual'), ...perpProductsV2, ...perpProductsPilot];
        const riskLimitMerged = [...riskLimits, ...convertRiskLimitV2(riskLimitsV2)];
        const contractsData = convertContracts(contracts, currenciesData, riskLimitMerged);

        const spots = products.filter((o) => o.type === 'Spot');
        const spotsData = convertSpots(spots, currencies);

        const riskLimitsData = convertRiskLimits(riskLimitMerged, contracts);

        return {
          currencies,
          contracts: contractsData,
          spots: spotsData,
          riskLimits: riskLimitsData,
          leverages,
          leveragesV2,
          collaterals,
          marginRiskLevels,
          tradeLevels,
          leverageMargins,
        };
      },
    });
  },
  getBaseConfig() {
    return http.get(API_URLS.PUBLIC_MISC, {
      formatter(data) {
        const {fiat} = data;
        fiat.map((currency) => {
          currency.text = currency.code;
          currency.value = currency.code;
          return currency;
        });
        return {fiat};
      },
    });
  },
  getFeeCondition(query) {
    return http.get(API_URLS.FEE_CONDITION, {query});
  },
  getPromotions(query) {
    return http.get(API_URLS.PROMOTIONS, {query});
  },
  getCoinBasicList() {
    return http.get(API_URLS.COIN_BASIC_LIST, {
      formatter(data) {
        const {rows} = data;
        rows.map((item) => {
          //充值提示是否需要标签  0-No tag/memo , 1-memo, 2-tag , 默认为 0
          const {addrExtra} = item;
          item.addrExtraStr = addrExtra ? (addrExtra === 1 ? 'Memo' : 'Tag') : '';
          item.depositOpen = !!item.depositOpen;
          item.withdrawOpen = !!item.withdrawOpen;
        });
        return rows;
      },
    });
  },
  getCoinInfoList(query) {
    return http.get(API_URLS.COIN_INFO_LIST, {query});
  },
  getProductsScale() {
    return http.get(API_URLS.PRODUCTS_SCALE, {});
  },
  getPrivateCoin() {
    return http.get(API_URLS.PRIVACY_COIN, {});
  },
  getCoinInfo(query) {
    return http.get(API_URLS.PRODUCTS_COIN_INFO, {query});
  },
  getCategoryList(query) {
    return http.get(API_URLS.CATEGORY_LIST, {
      query,
      formatter({rows}) {
        return rows.map((raw) => {
          const {coins, name, id, ...items} = raw;
          return {...items, text: name, value: id, id, coins: coins.map((o) => o.replace(' ', ''))};
        });
      },
    });
  },
  getCoinBase(query) {
    return http.get(API_URLS.COIN_BASIC, {query});
  },
  getFeeConfig(query = {}) {
    return http.get(API_URLS.SPOT_FEE_CONFIG, {query});
  },
  getFeeConfigList(query = {}) {
    return http.get(API_URLS.XPT_LEVEL_SIMPLE_CONFIG, {query});
  },
  getPilotCoins(query = null) {
    return http.get(API_URLS.PILOT_COIN_INFO, {query});
  },
};

function convertContracts(contracts, currencies, riskLimits) {
  return contracts.map((contract) => {
    const {settleCurrency, quoteCurrency, contractUnderlyingAssets, type} = contract;
    const isDoubleUnderlyingAssets = contractUnderlyingAssets.indexOf(' ') > -1 && numUtil.startWithNumber(contractUnderlyingAssets);
    let underlyingAssets = contractUnderlyingAssets;
    if (isDoubleUnderlyingAssets) {
      const tmpStr = contractUnderlyingAssets.split(' ');
      underlyingAssets = tmpStr[1];
    }
    const contractUnderlyingAssetsV2 = contractUnderlyingAssets.replaceAll(' ', '');
    const baseCurrency = settleCurrency === quoteCurrency ? underlyingAssets : settleCurrency;
    let settle = currencies.find((o) => o.currency === settleCurrency);
    if (!settle) {
      console.warn('not found config for ' + settleCurrency + ' settle currency in currencies list');
      settle = initCurrency(settleCurrency);
    }
    // home 额外的
    contract.contractTimes = 1;
    contract.isPerpetualV2 = type === 'PerpetualV2' || contractUtil.isPilotContract(type);

    const {valueScale: scale, displayCurrency: settleDisplayCurrency} = settle;
    const base = currencies.find((o) => o.currency === baseCurrency);
    const {steps} = riskLimits.find((o) => o.symbol === contract.symbol) || {}; // 特殊情况下币种未配置riskLimits信息
    const quote = currencies.find((o) => o.currency === quoteCurrency);
    contract.contractSide = settleCurrency === quoteCurrency ? 1 : -1;
    contract.baseCurrencyName = base ? base.name : baseCurrency;
    contract.baseCurrency = isDoubleUnderlyingAssets ? contractUnderlyingAssetsV2 : baseCurrency;
    contract.quoteCurrencyDisplay = quote ? quote.displayCurrency : quoteCurrency;
    contract.ratioFactor = Math.pow(10, contract.ratioScale);
    contract.ratioPrecision = 2;
    contract.valuePrecision = settleCurrency === 'USD' ? 2 : 4;
    contract.riskLimitSteps = steps;
    // TODO: confirm
    const amountUnit = contract.contractSide > 0 ? baseCurrency : quoteCurrency;
    const amountUnitV2 = contract.contractSide > 0 ? contractUnderlyingAssets : quoteCurrency;
    contract.pairName = settleCurrency === quoteCurrency ? contractUnderlyingAssetsV2 + settleDisplayCurrency : settleCurrency + quoteCurrency;
    contract.symbolV2 = contract.pairName;
    const isSupportHedge = contractUtil.isSupportHedgeMode(type);
    contract.isSupportHedge = isSupportHedge;
    //  TODO from config
    contract.qtyPrecision = isSupportHedge ? contract.qtyPrecision : 0;
    contract.contPrecision = isSupportHedge ? contract.qtyPrecision : 0;
    contract.settleValuePrecision = isSupportHedge ? contract.qtyPrecision : 8;
    contract.settleCurrencyDisplay = settleDisplayCurrency;
    contract.qtyScale = isSupportHedge ? 0 : 8;
    contract.qtyFactor = isSupportHedge ? 1 : 1e8;
    contract.contTickSizeEv = isSupportHedge ? Number(contract.qtyStepSize) : 1e8;
    contract.settleTickSizeEv = isSupportHedge ? Number(contract.qtyStepSize) : 1;
    contract.status = genContractStatus(contract);
    contract.contractSizePrecision = genContractSizePrecision(contract);
    contract.priceScale = isSupportHedge ? 0 : DEFAULT_USD_PRICE_SCALE;
    contract.priceFactor = isSupportHedge ? 1 : 1e4;
    contract.tickSizeEp = contract.tickSize * (isSupportHedge ? 1 : 1e4);
    const valueScale = isSupportHedge ? 0 : scale;
    contract.valueScale = valueScale;
    contract.valueFactor = Math.pow(10, valueScale);
    contract.contractSize = isSupportHedge ? 1 : contract.contractSize;
    contract.maxPriceEp = isSupportHedge ? Number(contract.maxPriceRp) : contract.maxPriceEp;
    contract.contAmount = isDoubleUnderlyingAssets ? amountUnitV2 : String(contract.contractSize) + amountUnit;
    contract.tickSizeEv = (settleCurrency === 'USD' ? 0.01 : 0.0001) * contract.valueFactor;
    contract.maxOrderQty = isSupportHedge ? Number(contract.maxOrderQtyRq) : contract.maxOrderQty;
    contract.tipOrderQty = isSupportHedge ? Number(contract.tipOrderQtyRq) : contract.tipOrderQty;
    contract.qtyStepSize = Number(contract.qtyStepSize || 0);
    contract.originType = contract.type;
    contract.urlSymbol = settleCurrency === quoteCurrency ? `${contractUnderlyingAssetsV2}-${settleDisplayCurrency}` : `${settleCurrency}-${quoteCurrency}`;
    if (contractUtil.isPilotContract(contract.type)) {
      contract.type = 'PerpetualV2';
    }
    delete contract.description;
    return contract;
  });
}

function convertSpots(spots, currencies) {
  return spots.map((spot) => {
    let base = currencies.find((o) => o.currency === spot.baseCurrency);
    let quote = currencies.find((o) => o.currency === spot.quoteCurrency);
    if (!base) {
      console.warn('not found config for ' + spot.baseCurrency + ' currency in currencies list');
      base = initCurrency(spot.baseCurrency);
    }
    if (!quote) {
      console.warn('not found config for ' + spot.quoteCurrency + ' currency in currencies list');
      quote = initCurrency(spot.quoteCurrency);
    }
    spot.indexSymbol = '.' + spot.baseCurrency;
    spot.baseCurrencyName = base.name;
    spot.quoteCurrencyName = quote.name;
    spot.baseQtyScale = base.valueScale;
    spot.quoteQtyScale = quote.valueScale;
    spot.baseQtyFactor = Math.pow(10, base.valueScale);
    spot.quoteQtyFactor = Math.pow(10, quote.valueScale);
    spot.baseDeltaSizeEv = spot.baseTickSizeEv;
    spot.baseDeltaSize = spot.baseTickSizeEv / 1e8;
    spot.quoteDeltaSizeEv = 100000000;
    spot.quoteDeltaSize = 1;
    spot.quoteQtyTickSizeEv = Number(Big(10).pow(Number(Big(spot.quoteQtyScale).minus(spot.quoteQtyPrecision))));
    spot.quoteQtyTickSize = Number(Big(10).pow(-spot.quoteQtyPrecision));
    spot.quoteTickSize = spot.quoteTickSizeEv / 1e8;
    spot.baseQtyTickSizeEv = spot.baseTickSizeEv;
    spot.baseQtyTickSize = spot.baseTickSizeEv / 1e8;
    spot.status = genSpotStatus(spot);
    spot.displayBaseCurrency = base.displayCurrency;
    spot.urlSymbol = `${spot.baseCurrency}-${spot.quoteCurrency}`;
    return spot;
  });
}

/**
 * 文档链接: http://confluence.cmexpro.com/pages/viewpage.action?pageId=48619469#Symbol%E7%BB%9F%E4%B8%80%E7%AE%A1%E7%90%86&%E5%BC%80%E7%9B%98%E5%80%92%E8%AE%A1%E6%97%B6%E7%B3%BB%E5%88%86-5.2.%E6%A0%B8%E5%BF%83%E9%A2%86%E5%9F%9F%E6%A8%A1%E5%9E%8B
 *
 * Init: 未上线。不可见
 * PreList: 预上线，在tv/market会有倒计时提示。可见不可交易
 * Listed: 上线交易中。可见可交易
 * Suspend: 仅可交易reduce only。可见部分可交易
 * Delisted: 下线 (目前配置 hideTime 与 delistTime 相同)。可见不可交易
 * Hide: 隐藏币对 (目前配置 hideTime 与 delistTime 相同)。不可见
 *
 * 业务状态: 不可见 -> 可见不可交易 -> 正常交易 -> 可见不可交易 -> 不可见
 * Status: Init -> PreList -> Listed -> Delisted -> Hide
 *
 * @typedef {'Init' | 'PreList' | 'Listed' | 'Delisted' | 'Hide'} SpotTradePairStatus
 * @typedef {'Init' | 'PreList' | 'Listed' | 'Suspend' | 'Delisted' | 'Hide'} ContractTradePairStatus
 */

/**
 * @param {*} item
 * @returns {ContractTradePairStatus}
 */
function genContractStatus(item) {
  const [displayTime, listTime, alertTime, delistTime, hideTime] = item.timeline || [0, 0, 0, 0, 0];
  const currentTime = Date.now();
  if (alertTime === 0 && delistTime === 0 && hideTime === 0) {
    if (currentTime < displayTime) {
      return 'Init';
    }
    if (currentTime < listTime) {
      return 'PreList';
    }
    return 'Listed';
  }
  if (currentTime < displayTime) {
    return 'Init';
  }
  if (currentTime < listTime) {
    return 'PreList';
  }
  if (currentTime < alertTime) {
    return 'Listed';
  }
  if (currentTime < delistTime) {
    return 'Suspend';
  }
  if (currentTime < hideTime) {
    return 'Delisted';
  }
  return 'Hide';
}

/**
 * @param {*} item
 * @returns {SpotTradePairStatus}
 */
function genSpotStatus(item) {
  const [displayTime, listTime, , delistTime, hideTime] = item.timeline || [0, 0, 0, 0, 0];
  const currentTime = Date.now();
  if (delistTime === 0 && hideTime === 0) {
    if (currentTime < displayTime) {
      return 'Init';
    }
    if (currentTime < listTime) {
      return 'PreList';
    }
    return 'Listed';
  }
  if (currentTime < displayTime) {
    return 'Init';
  }
  if (currentTime < listTime) {
    return 'PreList';
  }
  if (currentTime < delistTime) {
    return 'Listed';
  }
  if (currentTime < hideTime) {
    return 'Delisted';
  }
  return 'Hide';
}

function initCurrency(currency) {
  return {
    code: 1,
    currency: currency,
    depositOpen: true,
    displayCurrency: currency,
    maxValueEv: 5000000000000000000,
    minValueEv: 1,
    name: currency,
    needAddrTag: 0,
    qtyPrecision: 8,
    status: 'Listed',
    valueFactor: 100000000,
    valuePrecision: 8,
    valueScale: 8,
    withdrawOpen: true,
    inAssetsDisplay: 1,
  };
}

function convertCurrencies(currencies) {
  return currencies.map((item) => {
    item.symbol = item.currency;
    item.valueFactor = Math.pow(10, item.valueScale);
    return item;
  });
}

function convertRiskLimitV2(riskLimits) {
  return riskLimits.map((item) => {
    const {symbol, riskLimits, steps} = item;
    const riskLimitsData = riskLimits.map((riskLimit) => {
      const {limit, initialMarginRr, maintenanceMarginRr} = riskLimit;
      const riskLimitEv = Number(limit);
      const initialMarginEr = Number(initialMarginRr);
      const initialMargin = Big(initialMarginRr).times(100) + '%';
      const maintenanceMarginEr = Number(maintenanceMarginRr);
      const maintenanceMargin = Big(maintenanceMarginRr).times(100) + '%';
      return {limit, riskLimitEv, initialMarginEr, maintenanceMarginEr, initialMargin, maintenanceMargin};
    });

    return {symbol, steps, riskLimits: riskLimitsData};
  });
}

function convertRiskLimits(riskLimits, contracts) {
  return riskLimits.map((item) => {
    const {symbol, riskLimits, steps} = item;
    const contract = contracts.find((contract) => contract.symbol === symbol);
    if (!contract) {
      const riskLimitsData = riskLimits.map((riskLimit) => {
        riskLimit.riskLimitEv = 0;
        return riskLimit;
      });
      return {symbol, steps, riskLimits: riskLimitsData};
    }
    const riskLimitsData = riskLimits.map((riskLimit) => {
      riskLimit.riskLimitEv = riskLimit.limit * contract.valueFactor;
      return riskLimit;
    });

    return {symbol, steps, riskLimits: riskLimitsData};
  });
}

function genContractSizePrecision(contract) {
  if (contract.isSupportHedge) {
    return contract.qtyPrecision;
  }
  const {contractSize} = contract;
  if (contractSize && contractSize.toString().includes('.')) {
    return contractSize.toString().split('.')[1].length;
  }
  return 0;
}
